<!-- Previous script section remains unchanged -->
<script setup>
import { storyblokImage } from '~/utils/storyblok-image';
import tskPlaceholderIcon from '~/assets/svg/tsk.svg?url';
import { ref, computed, watch } from 'vue'
import { StoryblokComponent } from '@storyblok/vue'

const props = defineProps({
    blok: {
        type: Object,
        default: () => ({}),
    },
});

const { market } = useMarket()
const { markets } = useAppConfig()
const { t } = useI18n()

// Initialize serviceType based on Storyblok config
const serviceType = ref(props.blok.showCategorySwitch ? 'move' : (props.blok.defaultCategory || 'move'))
const distanceRange = ref('0-3') // Only for move &amp; deliver
const timeRange = ref('0-30') // Only for help at home (tsk)
const stairsEnabled = ref(false) // Controls if stairs are being used
const selectedStairLevel = ref(null) // Stores the selected level
const hasHelper = ref(false)

// Watch stairs enabled state to set initial level
watch(stairsEnabled, (newVal) => {
  if (newVal) {
    selectedStairLevel.value = '1'
  } else {
    selectedStairLevel.value = null
  }
})

// Computed value for price calculations that combines stairs state
const stairLevels = computed(() => {
  return stairsEnabled.value ? selectedStairLevel.value : false
})

const sizeImageFields = {
  S: 'sImage',
  M: 'mImage',
  L: 'lImage',
  XL: 'xlImage'
}

// Note: the help at home blok reuses the size fields of the size bloks, so its
// categories map onto sImage-xlImage in the order they are displayed
const tskImageFields = {
  assembly: 'sImage',
  mounting: 'mImage',
  carrying: 'lImage',
  garden: 'xlImage'
}

// The Storyblok blok and field names holding the images for each service type
const imageBloks = {
  move: { component: 'movingImages', fields: sizeImageFields },
  remove: { component: 'recyclingImages', fields: sizeImageFields },
  tsk: { component: 'tskImages', fields: tskImageFields }
}

const getImageBlock = (type) => {
  return (props.blok.sizeImages || []).find(block => block.component === imageBloks[type]?.component)
}

const getImageField = (item, type) => {
  return imageBloks[type]?.fields[item]
}

// Get image URL from Storyblok
const getImageUrl = (item, type) => {
  const image = getImageBlock(type)?.[getImageField(item, type)]

  // Note: bloks without help at home images yet fall back to a placeholder
  if (!image?.filename) return tskPlaceholderIcon

  return storyblokImage(image.filename, { width: 256 })
}

// Get description from Storyblok
const getDescription = (item, type) => {
  return getImageBlock(type)?.[`${getImageField(item, type)}Description`] || ''
}

// Help at home categories are named in code, sizes are only described by Storyblok
const getItemName = (item) => {
  return serviceType.value === 'tsk' ? t(`priceEstimate.tsk.${item}`) : ''
}

// Recommended prices in SEK, the same as the app's h2t and payAway base prices
const moveDeliverPrices = { S: 270, M: 420, L: 599, XL: 950 }
const removeRecyclePrices = { S: 270, M: 420, L: 599, XL: 950 }

// Move & deliver surcharges per size and distance on top of the recommended price
const distanceCosts = {
  S: { '0-3': 0, '3-20': 50, '20-40': 100, '40-70': 300 },
  M: { '0-3': 0, '3-20': 50, '20-40': 150, '40-70': 350 },
  L: { '0-3': 0, '3-20': 100, '20-40': 200, '40-70': 400 },
  XL: { '0-3': 0, '3-20': 200, '20-40': 400, '40-70': 600 }
}

// The price range goes from the recommended price up to 1.5 times it, as in the app
const getMaxPrice = (price) => Math.round(price * 1.5)

// Help at home price recommendations, per category and estimated time
const tskPrices = {
  assembly: {
    '0-30': { min: 350, max: 525 },
    '1-2': { min: 550, max: 825 },
    '2-3': { min: 1000, max: 1500 },
    '4+': { min: 2000, max: 3000 }
  },
  mounting: {
    '0-30': { min: 350, max: 525 },
    '1-2': { min: 550, max: 825 },
    '2-3': { min: 1000, max: 1500 },
    '4+': { min: 2000, max: 3000 }
  },
  carrying: {
    '0-30': { min: 300, max: 450 },
    '1-2': { min: 450, max: 675 },
    '2-3': { min: 600, max: 900 },
    '4+': { min: 1400, max: 2100 }
  },
  garden: {
    '0-30': { min: 300, max: 450 },
    '1-2': { min: 600, max: 900 },
    '2-3': { min: 1000, max: 1500 },
    '4+': { min: 1500, max: 2250 }
  }
}

// Currency conversion and symbol based on market
const currencyInfo = computed(() => {
  const currentMarket = market.value
  if (currentMarket === markets.uk) {
    return { symbol: '£', divider: 10 }
  } else if (currentMarket === markets.se) {
    return { symbol: 'kr', divider: 1 }
  } else if (currentMarket === markets.de || currentMarket === markets.pt || currentMarket === markets.at) {
    return { symbol: '€', divider: 10 }
  }
  // Default to euro if no market selected
  return { symbol: '€', divider: 10 }
})

// Convert price based on market
const convertPrice = (price) => {
  if (currencyInfo.value.divider === 1) return price
  return Math.round(price / currencyInfo.value.divider)
}

const sizes = ['S', 'M', 'L', 'XL']
const tskCategories = ['assembly', 'mounting', 'carrying', 'garden']

// Move & deliver and remove & recycle are priced by size, help at home by category
const items = computed(() => (serviceType.value === 'tsk' ? tskCategories : sizes))

const serviceTypes = computed(() => [
  { value: 'move', label: props.blok.movingTitle, gtm: 'pricetool-moving' },
  { value: 'remove', label: props.blok.recyclingTitle, gtm: 'pricetool-recycling' },
  { value: 'tsk', label: props.blok.tskTitle || t('priceEstimate.tsk.title'), gtm: 'pricetool-tsk' }
])

const distanceRanges = ['0-3', '3-20', '20-40', '40-70', '70+']
const timeRanges = [
  { value: '0-30', label: computed(() => t('priceEstimate.time.upTo30')) },
  { value: '1-2', label: computed(() => t('priceEstimate.time.oneToTwo')) },
  { value: '2-3', label: computed(() => t('priceEstimate.time.twoToThree')) },
  { value: '4+', label: computed(() => t('priceEstimate.time.fourPlus')) }
]
const stairLevelsOptions = [
  { value: '1', label: computed(() => t('priceEstimate.stairs.level', { count: 1 })) },
  { value: '2', label: computed(() => t('priceEstimate.stairs.level', { count: 2 })) },
  { value: '3', label: computed(() => t('priceEstimate.stairs.level', { count: 3 })) },
  { value: '4', label: computed(() => t('priceEstimate.stairs.level', { count: 4 })) },
  { value: '5', label: computed(() => t('priceEstimate.stairs.level', { count: 5 })) }
]

// Calculate stairs cost based on size and levels
const calculateStairsCost = (size, levels) => {
  const levelsNum = parseInt(levels)
  if (size === 'S' || size === 'M') {
    return 30
  } else if (size === 'L') {
    return levelsNum <= 2 ? 30 : 40 * (levelsNum - 2)
  } else { // XL
    return levelsNum <= 2 ? 50 : 70 * (levelsNum - 2)
  }
}

// Calculate price range for a specific size (or help at home category)
const getPriceRange = (size) => {
  if (serviceType.value === 'tsk') {
    // Help at home is priced by estimated time, stairs and helper don't apply
    const { min, max } = tskPrices[size][timeRange.value]

    return {
      min: convertPrice(min),
      max: convertPrice(max),
      symbol: currencyInfo.value.symbol,
      isLongDistance: false
    }
  }

  if (serviceType.value === 'move') {
    // Get base price for move & deliver
    const baseRange = distanceRange.value === '70+' ? '40-70' : distanceRange.value
    let price = moveDeliverPrices[size] + distanceCosts[size][baseRange]

    // Stairs impact
    if (stairLevels.value) {
      price += calculateStairsCost(size, stairLevels.value)
    }

    // Helper impact
    if (!hasHelper.value) {
      if (size === 'L') {
        price += 40
      } else if (size === 'XL') {
        price += 100
      }
    }

    const maxPrice = getMaxPrice(price)

    // For long distance moves (70+ km), use the adjusted max price as the minimum
    if (distanceRange.value === '70+') {
      return {
        min: convertPrice(maxPrice),
        max: null,
        symbol: currencyInfo.value.symbol,
        isLongDistance: true
      }
    }

    return {
      min: convertPrice(price),
      max: convertPrice(maxPrice),
      symbol: currencyInfo.value.symbol,
      isLongDistance: false
    }
  } else {
    // Remove & recycle pricing
    let price = removeRecyclePrices[size]

    // Stairs impact - using same logic as move & deliver
    if (stairLevels.value) {
      price += calculateStairsCost(size, stairLevels.value)
    }

    // Helper impact
    if (!hasHelper.value) {
      if (size === 'L' || size === 'XL') {
        price += 50
      }
    }

    return {
      min: convertPrice(price),
      max: convertPrice(getMaxPrice(price)),
      symbol: currencyInfo.value.symbol,
      isLongDistance: false
    }
  }
}
</script>

<template>
  <section class="bg-white dark:bg-space-blue" :id="blok.id ? blok.id : null">
    <div class="py-8 px-4 mx-auto max-w-screen-xl lg:px-12 text-center items-center">
      <div class="max-w-screen-xl mx-auto">
        <h1 class="mb-6 font-light text-gray-900 dark:text-white">
          {{ props.blok.title }}
        </h1>
        <p class="mb-8 font-light text-gray-500 lg:mb-8 dark:text-gray-400 sm:text-xl">
            {{ props.blok.description }}
        </p>

        <div class="block sm:space-x-8 py-4 bg-gradient-to-r to-gray-50 from-gray-200 dark:from-midnight-blue dark:to-deep-blue rounded-3xl">
            <div v-if="props.blok.showCategorySwitch" class="block sm:flex items-center mb-2 justify-center p-4 sm:pt-0">
              <h2 class="text-2xl font-medium text-center dark:text-white mr-3">{{ props.blok.categorySwitchTitle }} </h2>
              <ul class="flex mt-3 sm:mt-0 text-sm font-medium text-center justify-center text-gray-500 dark:text-gray-400">
                <li v-for="(type, index) in serviceTypes" :key="type.value" class="focus-within:z-10">
                  <a 
                    href="#" 
                    @click.prevent="serviceType = type.value"
                    :class="[
                      'inline-block p-2 sm:p-3 min-w-24 border-2 transition-all',
                      index === 0 ? 'rounded-l-lg' : '',
                      index === serviceTypes.length - 1 ? 'rounded-r-lg' : '',
                      serviceType === type.value
                        ? 'border-purple-berry bg-purple-berry-10 dark:bg-purple-berry/20 text-purple-berry dark:text-purple-berry-30'
                        : 'border-gray-300 dark:border-gray-700 dark:text-gray-500 hover:border-purple-berry-30 dark:hover:border-purple-berry'
                    ]"
                    :aria-current="serviceType === type.value ? 'page' : undefined"
                    :data-gtm="type.gtm"
                  >
                    {{ type.label }}
                  </a>
                </li>
              </ul>
            </div>
            
            <div class="space-y-6 px-4 sm:px-6">
              <!-- Price Range Grid -->
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                <div v-for="size in items" :key="size" class="p-4">
                  <div class="flex flex-col items-center">
                    <img 
                      :src="getImageUrl(size, serviceType)"
                      :alt="getItemName(size) || `Size ${size} illustration`"
                      class="h-24 w-24 sm:h-32 sm:w-32 object-contain"
                    />
                    <div class="text-center mb-1">
                      <p v-if="getItemName(size)" class="text-sm sm:text-base font-medium text-gray-900 dark:text-white mt-1">
                        {{ getItemName(size) }}
                      </p>
                      <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {{ getDescription(size, serviceType) }}
                      </p>
                    </div>
                    <div class="text-center">
                      <p class="text-2xl sm:text-3xl font-semibold text-minty-green font-serif tracking-normal">
                        <template v-if="getPriceRange(size).isLongDistance">
                          <template v-if="getPriceRange(size).symbol === 'kr'">
                            {{ getPriceRange(size).min }}+ {{ getPriceRange(size).symbol }}
                          </template>
                          <template v-else>
                            {{ getPriceRange(size).symbol }}{{ getPriceRange(size).min }}+
                          </template>
                        </template>
                        <template v-else>
                          <template v-if="getPriceRange(size).symbol === 'kr'">
                            {{ getPriceRange(size).min }}-{{ getPriceRange(size).max }} {{ getPriceRange(size).symbol }}
                          </template>
                          <template v-else>
                            {{ getPriceRange(size).symbol }}{{ getPriceRange(size).min }}-{{ getPriceRange(size).max }}
                          </template>
                        </template>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Long Distance Warning -->
              <div v-if="distanceRange === '70+' && serviceType === 'move'" class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mt-4">
                <p class="text-sm text-yellow-800 dark:text-yellow-200">
                  {{ props.blok.longDistanceWarning }}
                </p>
              </div>

              <!-- Distance Range (Move & Deliver only) -->
              <div v-if="serviceType === 'move'" class="mt-8">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
                  {{ props.blok.distanceDescription }}
                </label>
                <div class="flex text-sm flex-wrap gap-3">
                  <button
                    v-for="range in distanceRanges"
                    :key="range"
                    @click="distanceRange = range"
                    :class="[
                      'px-2 py-2 sm:py-3 rounded-lg border-2 sm:text-base transition-colors',
                      distanceRange === range
                        ? 'border-purple-berry bg-purple-berry-10 dark:bg-purple-berry/20 text-purple-berry dark:text-purple-berry-30'
                        : 'border-gray-300 dark:border-gray-700 dark:text-gray-500 hover:border-purple-300 dark:hover:border-purple-berry'
                    ]"
                    data-gtm="pricetool-change-range"
                  >
                    {{ range === '70+' ? '70+ km' : range + ' km' }}
                  </button>
                </div>
              </div>

              <!-- Time Range (Help at home only) -->
              <div v-if="serviceType === 'tsk'" class="mt-8">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-left">
                  {{ props.blok.timeDescription || t('priceEstimate.time.description') }}
                </label>
                <div class="flex text-sm flex-wrap gap-3">
                  <button
                    v-for="range in timeRanges"
                    :key="range.value"
                    @click="timeRange = range.value"
                    :class="[
                      'px-2 py-2 sm:py-3 rounded-lg border-2 sm:text-base transition-colors whitespace-nowrap',
                      timeRange === range.value
                        ? 'border-purple-berry bg-purple-berry-10 dark:bg-purple-berry/20 text-purple-berry dark:text-purple-berry-30'
                        : 'border-gray-300 dark:border-gray-700 dark:text-gray-500 hover:border-purple-300 dark:hover:border-purple-berry'
                    ]"
                    data-gtm="pricetool-change-time"
                  >
                    {{ range.label }}
                  </button>
                </div>
              </div>

              <!-- Stairs Selection -->
              <div v-if="serviceType !== 'tsk'" class="mt-8">
                <div class="flex items-start">
                  <label class="relative inline-flex items-start cursor-pointer">
                    <div class="flex items-center h-full">
                      <input 
                        type="checkbox" 
                        v-model="stairsEnabled"
                        class="sr-only peer"
                        data-gtm="pricetool-toggle-stairs"
                      >
                      <div class="w-11 min-w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-berry"></div>
                    </div>
                    <span class="ml-3 text-sm text-left text-gray-700 dark:text-gray-300">
                      {{ props.blok.toggleLevelDescription }}
                    </span>
                  </label>
                </div>
                <div v-if="stairsEnabled" class="flex flex-wrap gap-3 mt-3">
                  <button
                    v-for="option in stairLevelsOptions"
                    :key="option.value"
                    @click="selectedStairLevel = option.value"
                    :class="[
                      'px-2 py-2 sm:py-3 rounded-lg border-2 text-sm sm:text-base transition-colors whitespace-nowrap',
                      selectedStairLevel === option.value
                        ? 'border-purple-berry bg-purple-berry-10 dark:bg-purple-berry/20 text-purple-berry dark:text-purple-berry-30'
                        : 'border-gray-300 dark:border-gray-700 dark:text-gray-500 hover:border-purple-berry-30 dark:hover:border-purple-berry'
                    ]"
                    data-gtm="pricetool-change-stairs"
                  >
                    {{ option.label }}
                  </button>
                </div>
              </div>

              <!-- Helper Toggle hiding it to begin with
              <div class="flex items-start mt-8">
                <label class="relative inline-flex items-start cursor-pointer">
                  <div class="flex items-center h-full">
                    <input 
                      type="checkbox" 
                      v-model="hasHelper"
                      class="sr-only peer"
                      data-gtm="pricetool-toggle-help"
                    >
                    <div class="w-11 min-w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-berry"></div>
                  </div>
                  <span class="ml-3 text-sm text-gray-700 dark:text-gray-300 text-left">
                    {{ props.blok.toggleHelpDescription }}
                  </span>
                </label>
              </div>
               -->
              <!-- Hiding this for now as it is not needed with current title and descriptioin
              <div class="mb-6">
                <p class="text-sm mb-6 text-gray-500 dark:text-gray-400 mt-8 text-center">
                  {{ t('priceEstimate.disclaimer') }}
                </p>
              </div>
              -->
              
              <!-- CTA Section -->
              <div v-if="props.blok.ctas && props.blok.ctas.length > 0" class="flex flex-wrap justify-center gap-4 py-6">
                <template v-for="cta in props.blok.ctas" :key="cta._uid">
                  <StoryblokComponent 
                    :blok="cta"
                  />
                </template>
              </div>

            </div>
        </div>
      </div>
    </div>
  </section>
</template>
